import { useState, useEffect } from "react";
import { Crown, Check, ArrowLeft, CreditCard, Shield, Zap, ExternalLink, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";

import { AppSidebar } from "@/components/AppSidebar";
import { Link } from "react-router-dom";

const Subscription = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  // Initialize sidebar state from localStorage, default to true (expanded)
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sidebarState');
    return saved ? JSON.parse(saved) : true;
  });

  // Save sidebar state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sidebarState', JSON.stringify(isSidebarOpen));
  }, [isSidebarOpen]);
  const [subscriptionData, setSubscriptionData] = useState<{
    subscribed: boolean;
    subscription_tier: string | null;
    subscription_end: string | null;
    reason?: string;
  } | null>(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);

  // Check subscription status on component mount and when returning from Stripe
  useEffect(() => {
    if (user) {
      checkSubscriptionStatus();
    }
  }, [user]);

  // Check for success/cancel parameters from Stripe redirect
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isSuccess = urlParams.get('success') === 'true';
    const isCanceled = urlParams.get('canceled') === 'true';
    
    if (isSuccess) {
      toast.success('Zahlung erfolgreich! Ihr Abonnement wird aktiviert.');
      // Refresh subscription status after successful payment
      setTimeout(() => {
        checkSubscriptionStatus();
      }, 2000);
      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (isCanceled) {
      toast.error('Zahlung abgebrochen.');
      // Clean up URL parameters  
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const checkSubscriptionStatus = async () => {
    if (!user) return;
    
    setIsLoadingSubscription(true);
    try {
      const { data, error } = await supabase.functions.invoke('check-subscription');

      if (error) {
        console.error('Subscription check error:', error);
        toast.error('Fehler beim Laden des Subscription-Status');
      } else {
        setSubscriptionData(data);
      }
    } catch (error) {
      console.error('Subscription check failed:', error);
      toast.error('Verbindungsfehler beim Laden der Subscription-Daten');
    } finally {
      setIsLoadingSubscription(false);
    }
  };

  const handleManageSubscription = async () => {
    if (!user || !subscriptionData?.subscribed || subscriptionData.reason === 'Organization Owner') {
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');

      if (error) {
        console.error('Customer portal error:', error);
        toast.error('Fehler beim Öffnen des Subscription-Portals');
      } else if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Customer portal failed:', error);
      toast.error('Verbindungsfehler beim Öffnen des Portals');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpgrade = async (plan: 'helpdesk' | 'pro') => {
    if (!user) {
      toast.error('Sie müssen angemeldet sein, um ein Abonnement abzuschließen');
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { plan }
      });

      if (error) {
        console.error('Checkout error:', error);
        toast.error('Fehler beim Erstellen der Checkout-Session');
      } else if (data?.url) {
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        toast.error('Keine Checkout-URL erhalten');
      }
    } catch (error) {
      console.error('Checkout failed:', error);
      toast.error('Verbindungsfehler beim Starten des Checkout-Prozesses');
    } finally {
      setIsLoading(false);
    }
  };

  const formatRenewalDate = (dateString: string | null) => {
    if (!dateString) return null;
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit', 
        year: 'numeric'
      });
    } catch (error) {
      return null;
    }
  };

  const isPremiumUser = subscriptionData?.subscribed || false;
  const isOrganizationOwner = subscriptionData?.reason === 'Organization Owner';
  const renewalDate = formatRenewalDate(subscriptionData?.subscription_end);

  const features = [
    "Unbegrenzter Zugang zu allen Premium-Inhalten",
    "Exklusive Marketing-Strategien und Geschäftspläne", 
    "Erweiterte technische Dokumentation",
    "Prioritäts-Support über AI-Chat",
    "Früher Zugang zu neuen Features und Updates",
    "Erweiterte Suchfunktionen mit Filtern",
    "Export-Funktionen für alle Inhaltstypen",
    "Detaillierte Analyse-Tools und Reports",
    "Offline-Zugang zu wichtigen Dokumenten",
    "Benutzerdefinierte Content-Kategorien"
  ];

  return (
    <SidebarProvider open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
      <div className="min-h-screen flex w-full bg-gradient-subtle">
        {/* Sidebar */}
        <AppSidebar />
        
        {/* Main Content */}
        <div className="flex-1 flex flex-col">
          {/* Top Bar */}
          <header className="h-12 flex items-center border-b bg-card/80 backdrop-blur-sm sticky top-0 z-40">
            <SidebarTrigger className="ml-2" />
            <div className="flex-1 flex items-center justify-between px-4">
              <div>
                <h1 className="text-lg font-bold">Premium-Mitgliedschaft</h1>
              </div>
              <div className="flex items-center gap-4">
                <Link to="/subscription">
                  <Button variant="outline" size="sm" className="gap-2">
                    <Crown className="h-4 w-4" />
                    Premium
                  </Button>
                </Link>
              </div>
            </div>
          </header>

          {/* Content Area */}
          <div className="flex-1 overflow-auto">
            <div className="container mx-auto px-4 py-8">
              <div className="max-w-4xl mx-auto">
                {/* Page Title */}
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-2">
                    Schalten Sie alle Premium-Features frei
                  </h2>
                  <p className="text-lg text-muted-foreground">
                    Wählen Sie Ihren passenden Plan
                  </p>
                </div>
          
          {/* Current Status */}
          {isLoadingSubscription ? (
            <Card className="mb-8">
              <CardContent className="py-8">
                <div className="flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2" />
                  <span>Lade Subscription-Status...</span>
                </div>
              </CardContent>
            </Card>
          ) : isPremiumUser ? (
            <Card className="mb-8 border-success bg-gradient-to-r from-success/5 to-success/10">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Crown className="h-6 w-6 text-success" />
                  <CardTitle className="text-success">
                    {subscriptionData?.subscription_tier === 'HelpCenter' ? 'HelpCenter-Mitglied' : 'Premium-Mitglied'}
                  </CardTitle>
                </div>
                <CardDescription>
                  {isOrganizationOwner 
                    ? 'Als Teamleiter haben Sie automatisch Zugang zum HelpCenter!'
                    : 'Sie haben bereits Zugang zu allen Premium-Features!'
                  }
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="space-y-1">
                    {renewalDate && !isOrganizationOwner ? (
                      <>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <p className="text-sm font-medium">
                            Verlängert sich am: {renewalDate}
                          </p>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Plan: {subscriptionData?.subscription_tier} (39,99€/Monat)
                        </p>
                      </>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Crown className="h-4 w-4 text-success" />
                        <p className="text-sm font-medium text-success">
                          {isOrganizationOwner ? 'Teamleiter-Berechtigung' : 'Dauerhafter Zugang'}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {!isOrganizationOwner && (
                    <div className="flex flex-col gap-2">
                      <Button 
                        variant="outline" 
                        onClick={handleManageSubscription}
                        disabled={isLoading}
                        className="gap-2"
                      >
                        {isLoading ? (
                          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <ExternalLink className="h-4 w-4" />
                        )}
                        Abonnement verwalten
                      </Button>
                      {renewalDate && (
                        <p className="text-xs text-muted-foreground text-center">
                          Kündigen
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="mb-8 border-premium/30 bg-gradient-to-br from-background to-premium/5">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Crown className="h-6 w-6 text-premium" />
                  <CardTitle className="text-premium">Kostenloses Konto</CardTitle>
                </div>
                <CardDescription>
                  Upgraden Sie jetzt für Vollzugang zum Vodafone Archiv
                </CardDescription>
              </CardHeader>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* HelpCenter Plan */}
            <Card className="relative border-premium/30 bg-gradient-to-br from-background to-premium/5">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <Badge className="bg-gradient-premium text-white px-4 py-1">
                  Wissensbank
                </Badge>
              </div>
              
              <CardHeader className="pt-8">
                <CardTitle className="text-xl flex items-center gap-2">
                  <Crown className="h-5 w-5 text-premium" />
                  HelpCenter
                </CardTitle>
                <CardDescription>Vollzugriff zur Organisationswissensbank</CardDescription>
                <div className="flex items-baseline gap-1 pt-4">
                  <span className="text-sm text-muted-foreground line-through">39,99€</span>
                  <span className="text-3xl font-bold text-premium">29,99€</span>
                  <span className="text-muted-foreground">/erstes Monat</span>
                </div>
                <p className="text-sm text-muted-foreground">Danach 39,99€/Monat</p>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-2">
                  {features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-success flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <Separator />
                
                <Button 
                  onClick={() => handleUpgrade('helpdesk')}
                  disabled={isLoading || (subscriptionData?.subscription_tier === 'HelpCenter')}
                  className="w-full gap-2" 
                  variant="premium" 
                  size="lg"
                >
                  {isLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : subscriptionData?.subscription_tier === 'HelpCenter' ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Crown className="h-4 w-4" />
                  )}
                  {isLoading ? "Wird verarbeitet..." : 
                   subscriptionData?.subscription_tier === 'HelpCenter' ? "Aktiv" : "HelpCenter buchen"}
                </Button>
              </CardContent>
            </Card>

            {/* Pro+ Plan */}
            <Card className="relative border-2 border-premium bg-gradient-to-br from-premium/5 to-premium/10">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <Badge className="bg-premium text-white px-4 py-1">
                  Der Game Changer
                </Badge>
              </div>
              
              <CardHeader className="pt-8">
                <CardTitle className="text-xl flex items-center gap-2">
                  <Crown className="h-5 w-5 text-premium" />
                  Pro+
                </CardTitle>
                <CardDescription>Kundenverwaltung & professionelle Tools</CardDescription>
                <div className="flex items-baseline gap-1 pt-4">
                  <span className="text-3xl font-bold text-premium">59,99€</span>
                  <span className="text-muted-foreground">/Monat</span>
                </div>
                <p className="text-sm text-success font-medium">3 Tage kostenlos testen</p>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-2">
                  <div className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-success flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Kundenverwaltung mit Stammdaten</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-success flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Bedarfsanalyse & Dokument-Upload</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-success flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Digitales Protokoll & Notizen</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-success flex-shrink-0 mt-0.5" />
                    <span className="text-sm">To-Do-Management mit Erinnerungen</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-success flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Automatische Termin-Reminder</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-success flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Kundenspezifische Workflows</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-success flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Erweiterte CRM-Funktionen</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-success flex-shrink-0 mt-0.5" />
                    <span className="text-sm">Performance & Analytics Dashboard</span>
                  </div>
                </div>
                
                <Separator />
                
                <Button 
                  onClick={() => handleUpgrade('pro')}
                  disabled={isLoading || (subscriptionData?.subscription_tier === 'Pro+')}
                  className="w-full gap-2" 
                  variant="premium" 
                  size="lg"
                >
                  {isLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : subscriptionData?.subscription_tier === 'Pro+' ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Crown className="h-4 w-4" />
                  )}
                  {isLoading ? "Wird verarbeitet..." : 
                   subscriptionData?.subscription_tier === 'Pro+' ? "Aktiv" : "Pro+ buchen"}
                </Button>
              </CardContent>
            </Card>
          </div>


          {/* Additional Information */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-success" />
                  <CardTitle className="text-lg">Sicher & Zuverlässig</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Ihre Daten sind durch modernste Verschlüsselung geschützt. 
                  Vodafone-Standard Sicherheit garantiert.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-primary" />
                  <CardTitle className="text-lg">Flexible Zahlung</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Jederzeit kündbar. 14 Tage Geld-zurück-Garantie. 
                  Sichere Zahlung über verschlüsselte Verbindungen.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-premium" />
                  <CardTitle className="text-lg">Sofortiger Zugang</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Nach dem Upgrade haben Sie sofort Zugang zu allen 
                  Premium-Inhalten und erweiterten Features.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* FAQ Section */}
          <Card className="mt-8">
            <CardHeader>
              <CardTitle>Häufig gestellte Fragen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-medium text-foreground mb-1">
                  Kann ich beide Subscriptions unabhängig buchen?
                </h4>
                <p className="text-sm text-muted-foreground">
                  Ja, Sie können HelpCenter und Pro+ einzeln oder zusammen buchen. 
                  Jede Subscription kann unabhängig verwaltet und gekündigt werden.
                </p>
              </div>
              
              <Separator />
              
              <div>
                <h4 className="font-medium text-foreground mb-1">
                  Was passiert mit meinen Daten nach der Kündigung?
                </h4>
                <p className="text-sm text-muted-foreground">
                  Ihre erstellten Inhalte bleiben erhalten. Sie haben weiterhin 
                  Zugang zu grundlegenden Features, aber nicht mehr zu den Premium-Funktionen.
                </p>
              </div>
              
              <Separator />
              
              <div>
                <h4 className="font-medium text-foreground mb-1">
                  Gibt es Mengenrabatte für Teams?
                </h4>
                <p className="text-sm text-muted-foreground">
                  Ja, für Teams ab 10 Personen bieten wir spezielle Tarife an. 
                  Kontaktieren Sie unseren Support für weitere Informationen.
                </p>
              </div>
            </CardContent>
          </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default Subscription;